import { getData } from '../get-table'
import { HttpService } from '../http.service'
import { usePayrollStore } from '@/store/payroll-data'

const http = new HttpService()

export interface LiquidationRequest {
  leaveReasonId: string
  employees: LiquidationRequestEmployee[]
  leaveReasonConcepts: string[]
  extraConcepts: string[]
  leaveDate: string
}

export interface adjustsConcepts {
  quantity: number
  amount: number
  adjustQuantity: number
  adjustAmount: number
  adjustAverage: string[]
}

export interface LiquidationRequestEmployee {
  isRecontractable: boolean
  observation: string
  conceptsToAdd: string[]
  adjusts: adjustsConcepts[]
}

export interface ILiquidationReport {
  code: string
  parameters: {
    templateId: string
    liquidationRequestId: string
    paymentCurrency: {
      coinId: string
      exchangeRate: number
    }
    employees: string[]
  }
}

export async function getLiquidationRequests(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = ''
): Promise<any> {
  // where's
  const where = ['liquidation_request.payroll_id = :payrollId']
  if (search) {
    where.push(
      `CONCAT(liquidation_request.leave_date,' ',liquidation_request.updated_at,' ',leave_reasons.name,' ',employees.name,' ',employees.code) ILIKE '%${search}%'`
    )
  }
  // order by
  let modifier = []
  dataOrder !== ''
    ? (modifier = [
        'group by liquidation_request.id, leave_reasons.name, liquidation_request.leave_date, process_status.code, process_status.name, liquidation_request.updated_at',
        `order by ${dataOrder[1]} ${dataOrder[0]}`
      ])
    : (modifier = [
        'group by liquidation_request.id, leave_reasons.name, liquidation_request.leave_date, process_status.code, process_status.name, liquidation_request.updated_at',
        'order by liquidation_request.updated_at desc'
      ])

  where.push(
    `jsonb_typeof(liquidation_request.employees) = 'array' AND jsonb_array_length(liquidation_request.employees) > 0 AND (liquidation_request.employees->0->>'id') IS NOT NULL`
  )

  // result
  const response: any = await getData({
    tables: ['pg_liquidation_request'],
    fields: [
      'liquidation_request.id',
      'leave_reasons.name as leave_reason',
      'liquidation_request.leave_date',
      "json_agg(CONCAT(employees.code,'-',employees.name)) as employees",
      'process_status.code as status_code',
      'process_status.name as status_name',
      'liquidation_request.updated_at'
    ],
    join: [
      'INNER JOIN process_status ON liquidation_request.status_id = process_status.id',
      'INNER JOIN leave_reasons ON liquidation_request.leave_reason_id = leave_reasons.id',
      "LEFT JOIN LATERAL (SELECT  jsonb_array_elements(liquidation_request.employees)->>'id' AS employee_id) AS employee ON true",
      'LEFT JOIN employees ON employee.employee_id::uuid = employees.id'
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response
}

export async function getLiquidationRequest(
  id: string
): Promise<LiquidationRequest> {
  const templateId = await filterTemplates()
  const coinId = await getPayrollCurrency()
  const data = await getData<any>({
    tables: ['pg_liquidation_request'],
    fields: [
      'liquidation_request.id',
      'liquidation_request.leave_date',
      "jsonb_agg(DISTINCT jsonb_build_object('id',employees.id,'name_employee',employees.name,'name', CONCAT(employees.code,' - ',employees.name), 'identification_number', employees.identification_number,'joining_date',employees.joining_date)) AS employee_info",
      "jsonb_agg(DISTINCT jsonb_build_object('id',concepts.id,'name',CONCAT(concepts.code,' - ',concepts.name))) AS concept_info",
      'liquidation_request.employees',
      'liquidation_request.is_deleted',
      'liquidation_request.updated_at',
      'process_status.code as status_code',
      'leave_reasons.name as leave_reason',
      'currencies.name as currency',
      'frequencies.name as frequency'
      //'liquidation_request.custom_fields as custom'
    ],
    join: [
      'INNER JOIN process_status ON liquidation_request.status_id = process_status.id',
      'INNER JOIN leave_reasons ON liquidation_request.leave_reason_id = leave_reasons.id',
      'INNER JOIN payrolls ON liquidation_request.payroll_id = payrolls.id',
      'INNER JOIN currencies ON payrolls.currency_id = currencies.id',
      'INNER JOIN frequencies ON payrolls.frequency_id = frequencies.id',
      'CROSS JOIN LATERAL jsonb_array_elements(liquidation_request.employees) AS employee_json',
      "CROSS JOIN LATERAL jsonb_array_elements(employee_json->'concepts') AS concept_json",
      "JOIN employees ON employees.id = (employee_json->>'id')::uuid",
      "JOIN concepts ON concepts.id = (concept_json->>'id')::uuid"
    ],
    where: [`liquidation_request.id = '${id}'`],
    modifier: [
      'GROUP BY liquidation_request.id, process_status.code, process_status.name, leave_reasons.name, currencies.name, frequencies.name'
    ],
    options: {
      outputFormat: 'json',
      pagination: { offset: 0, limit: 10 }
    }
  })
  const payloadGenerateLiquidationReport: ILiquidationReport = {
    code: 'basics/liquidation-slip',
    parameters: {
      templateId: templateId,
      liquidationRequestId: id,
      paymentCurrency: {
        coinId: coinId,
        exchangeRate: 1
      },
      employees: []
    }
  }
  if (data[0].results.length > 0)
    data[0].results[0].reportLiquidationEmployee =
      payloadGenerateLiquidationReport

  return data[0].results[0]
}

export async function getMovements(idLiquidation: string, idEmployee: string) {
  const response: any = await http.get(
    'liquidation/request/employee/' + idLiquidation + '/' + idEmployee
  )

  if (response?.data?.data?.error) {
    return false
  }
  return response.data
}

export async function getLiquidationStatus(liquidation: string) {
  const data = await getData<any>({
    tables: ['pg_process_requests'],
    fields: ['liquidation_request_id, process_status.code as status'],
    join: [
      'LEFT JOIN process_status ON process_status.id = process_requests.process_status_id'
    ],
    where: [`liquidation_request_id ='${liquidation}'`],
    modifier: ['order by process_requests.updated_at desc']
  })

  return data[0].results[0] || null
}

export async function getLeaveReasons() {
  const data = await getData<any>({
    tables: ['pg_leave_reasons'],
    fields: ['id', "CONCAT(code,'-',name) as name", 'master_concepts'],
    where: ['country_id=:countryId']
  })

  return data[0].results
}

export async function getConcepts() {
  const processes = await getData<any>({
    tables: ['pg_processes'],
    fields: ['id'],
    where: ["code = 'L'"]
  })
  const data = await getData<any>({
    tables: ['pg_concepts'],
    fields: [
      'master_concepts.id as master_concept_id',
      'concepts.id',
      'master_concepts.name as master_concept_name',
      "CONCAT(concepts.code,'-',concepts.name) as name"
    ],
    join: [
      'LEFT JOIN master_concepts ON concepts.master_concept_id = master_concepts.id'
    ],
    where: [
      'concepts.is_inactive = false',
      'concepts.is_deleted = false',
      'concepts.payroll_id = :payrollId',
      `(jsonb_exists(master_concepts.applies_to, '${processes[0].results[0].id}') OR jsonb_exists(concepts.processes, '${processes[0].results[0].id}'))`
    ]
  })

  return data[0].results
}

export async function getEmployees() {
  const data = await getData<any>({
    tables: ['pg_employees'],
    fields: [
      'distinct employees.id',
      'employees.name',
      'directions.name as direccion',
      'managements.name as gerencia',
      'departments.name as departamento',
      'sucursals.name as sucursal',
      'positions.name as cargo',
      'turns.name as turno',
      'employees.leave_date as fechaSalida',
      'employees.code as codigo',
      "CONCAT(employees.code,' - ',employees.name) as codigo_name",
      'false as isChecked'
    ],
    join: [
      'LEFT JOIN directions ON employees.direction_id=directions.id',
      'LEFT JOIN managements ON employees.management_id=managements.id',
      'LEFT JOIN departments ON employees.department_id=departments.id',
      'LEFT JOIN sucursals ON employees.sucursal_id=sucursals.id',
      'LEFT JOIN positions ON employees.position_id=positions.id',
      'LEFT JOIN turns ON employees.turn_id=turns.id'
    ],
    where: [
      'employees.is_deleted=false',
      `employees.payroll_id =:payrollId`,
      `employees.leave_date is null`
    ],
    options: {
      outputFormat: 'json'
    }
  })

  return data[0].results
}

export async function createLiquidationRequest(
  data: LiquidationRequest
): Promise<boolean> {
  const response: any = await http.post('liquidation/request', {
    leaveReasonId: data.leaveReasonId,
    employees: data.employees,
    leaveReasonConcepts: data.leaveReasonConcepts,
    extraConcepts: data.extraConcepts,
    leaveDate: data.leaveDate
  })

  if (response?.data?.data?.error) {
    return false
  }
  return response.data.id
}

export async function updateLiquidationRequestEmployee(
  idLiquidation: string,
  idEmployee: string,
  data: LiquidationRequestEmployee
): Promise<boolean> {
  const response: any = await http.put(
    'liquidation/request/employee/' + idLiquidation + '/' + idEmployee,
    {
      isRecontractable: data.isRecontractable,
      observation: data.observation,
      conceptsToAdd: data.conceptsToAdd,
      adjusts: data.adjusts
    }
  )

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function deleteLiquidationRequest(id: string): Promise<boolean> {
  const response: any = await http.delete('liquidation/request/' + id)

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function deleteLiquidationRequestEmployee(
  idLiquidation: string,
  idEmployee: string
): Promise<boolean> {
  const response: any = await http.delete(
    'liquidation/request/employee/' + idLiquidation + '/' + idEmployee
  )

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function reprocessLiquidationRequestEmployee(
  idLiquidation: string,
  idEmployee: string
): Promise<boolean> {
  const response: any = await http.put(
    'liquidation/request/reprocess/' + idLiquidation + '/' + idEmployee,
    {}
  )

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function closeLiquidationRequest(id: string): Promise<boolean> {
  const response: any = await http.put('liquidation/request/close/' + id, {})

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function getTemplates(): Promise<any> {
  const where = [
    "(receipt_templates.country_id=:countryId AND  processes.code='L' AND ((companies.id IS NUll AND payrolls.id IS NULL  AND receipt_templates.is_standard = true) OR (companies.id =:companyId  AND (payrolls.id IS NULL OR payrolls.id =:payrollId))))"
  ]
  const response: any = await getData({
    tables: ['pg_receipt_templates'],
    fields: [
      'receipt_templates.id as templateId',
      'receipt_templates.code',
      'receipt_templates.name',
      'receipt_templates.is_standard as isStandard',
      'processes.code as process',
      'payrolls.id as payrollId',
      'companies.id as companyId',
      'receipt_templates.country_id as countryId'
    ],
    where,
    join: [
      'LEFT JOIN LATERAL jsonb_array_elements_text(receipt_templates.payrolls) p(payroll) ON TRUE',
      'LEFT JOIN payrolls ON payrolls.id = p.payroll::uuid',
      'LEFT JOIN LATERAL jsonb_array_elements_text(receipt_templates.process) pr(process) ON TRUE',
      'LEFT JOIN processes ON processes.id = pr.process::uuid',
      'LEFT JOIN LATERAL jsonb_array_elements_text(receipt_templates.companies) c(company) ON TRUE',
      'LEFT JOIN companies ON companies.id = c.company::uuid'
    ],
    modifier: ['order by receipt_templates.created_at desc'],
    options: {
      outputFormat: 'json'
    }
  })
  return response[0].results
}

export async function filterTemplates(): Promise<string> {
  const listTemplates = await getTemplates()

  if (listTemplates.length > 0) {
    return listTemplates[0].templateId
  }
  return ''
}

export async function getPayrollCurrency() {
  const fields = []
  const where = [`id=:payrollId`, 'is_deleted = false']

  fields.push('currency_id as coinId')
  const data = await getData<any>({
    tables: ['pg_payrolls'],
    fields,
    where: where
  })

  return data[0].results[0].coinId
}

export async function generateLiquidationReport(
  data: ILiquidationReport
): Promise<string> {
  const response: any = await http.post('reports/', {
    code: data.code,
    parameters: {
      templateId: data.parameters.templateId,
      liquidationRequestId: data.parameters.liquidationRequestId,
      employees: data.parameters.employees,
      paymentCurrency: data.parameters.paymentCurrency
    }
  })

  if (response?.data?.data?.error) {
    return response?.data?.data?.error
  }
  return response?.data
}

/**
 * Consultar solicitud de reporte
 */
export async function getReportRequest(id: string) {
  const data = await getData<any>({
    tables: ['pg_reports_requests'],
    fields: [
      'reports_requests.errors',
      'process_status.name as status',
      'reports_requests.url'
    ],
    join: [
      'INNER JOIN process_status ON reports_requests.status_id=process_status.id'
    ],
    where: [`reports_requests.id ='${id}'`]
  })

  return data[0].results[0]
}
